import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import type { components } from '@/api/types.gen';

export type Customer = components['schemas']['Customer'];
export type CustomerCreate = components['schemas']['CustomerCreate'];
export type CustomerUpdate = components['schemas']['CustomerUpdate'];
export type CustomerPage = components['schemas']['CustomerPage'];
export type LinkToken = components['schemas']['LinkToken'];
export type Message = components['schemas']['Message'];
export type MessageSendRequest = components['schemas']['MessageSendRequest'];

export interface CustomersQueryParams {
  page: number;
  pageSize: number;
  linked?: 'all' | 'true' | 'false';
  /** true → корзина (soft-deleted клиенты). При deleted linked игнорируется. */
  deleted?: boolean;
}

function buildCustomersUrl({ page, pageSize, linked, deleted }: CustomersQueryParams): string {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (deleted) {
    params.set('deleted', 'true');
  } else if (linked === 'true' || linked === 'false') {
    params.set('linked', linked);
  }
  return `/api/v1/admin/customers?${params.toString()}`;
}

export function useCustomersQuery(params: CustomersQueryParams) {
  return useQuery({
    queryKey: ['customers', params],
    queryFn: () => api.get<CustomerPage>(buildCustomersUrl(params)),
    placeholderData: (prev) => prev,
  });
}

export function useCustomerQuery(customerId: string | undefined) {
  return useQuery({
    enabled: Boolean(customerId),
    queryKey: ['customers', 'detail', customerId],
    queryFn: () => api.get<Customer>(`/api/v1/admin/customers/${customerId}`),
  });
}

export function useCreateCustomerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['customers', 'create'],
    mutationFn: (input: CustomerCreate) => api.post<Customer>('/api/v1/admin/customers', input),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      qc.setQueryData(['customers', 'detail', created.id], created);
    },
  });
}

export interface UpdateCustomerInput {
  id: string;
  patch: CustomerUpdate;
}

export function useUpdateCustomerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['customers', 'update'],
    mutationFn: ({ id, patch }: UpdateCustomerInput) =>
      api.patch<Customer>(`/api/v1/admin/customers/${id}`, patch),
    onSuccess: (updated) => {
      qc.setQueryData(['customers', 'detail', updated.id], updated);
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useDeleteCustomerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['customers', 'delete'],
    mutationFn: (id: string) => api.delete<void>(`/api/v1/admin/customers/${id}`),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: ['customers', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useRestoreCustomerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['customers', 'restore'],
    mutationFn: (id: string) => api.post<Customer>(`/api/v1/admin/customers/${id}/restore`),
    onSuccess: (restored) => {
      qc.setQueryData(['customers', 'detail', restored.id], restored);
      // Инвалидируем и активный список, и корзину — оба под ключом ['customers', ...].
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useIssueLinkTokenMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['customers', 'link-token'],
    mutationFn: (customerId: string) =>
      api.post<LinkToken>(`/api/v1/admin/customers/${customerId}/link-tokens`),
    onSuccess: (_data, customerId) => {
      // Если клиент в этот момент привязался — он мог приехать с linked_at;
      // на всякий случай освежим карточку.
      void qc.invalidateQueries({ queryKey: ['customers', 'detail', customerId] });
    },
  });
}

export interface SendMessageInput {
  customerId: string;
  body: string;
  idempotencyKey: string;
}

export function useSendMessageMutation() {
  return useMutation({
    mutationKey: ['messages', 'send'],
    mutationFn: ({ customerId, body, idempotencyKey }: SendMessageInput) =>
      api.post<Message>(
        '/api/v1/admin/messages',
        { customer_id: customerId, body },
        { headers: { 'Idempotency-Key': idempotencyKey } },
      ),
  });
}

export function useMessageStatusQuery(messageId: string | null, enabled: boolean) {
  return useQuery({
    enabled: Boolean(messageId) && enabled,
    queryKey: ['messages', 'detail', messageId],
    queryFn: () => api.get<Message>(`/api/v1/admin/messages/${messageId}`),
    refetchInterval: (query) => {
      const data = query.state.data as Message | undefined;
      if (!data) return 1500;
      if (data.status === 'queued' || data.status === 'sending') return 1500;
      return false;
    },
    refetchIntervalInBackground: false,
  });
}
